import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import _ from 'underscore';
import { useDebounce } from 'use-debounce';
import api from 'helpers/api';
import Input from '../components/input';
import sources from 'helpers/sources';
import contactBadgeClass from 'helpers/contact_badge_class';
import useGym from 'helpers/use_gym';
import Dropdown from 'react/components/dropdown';
import redirect from 'helpers/redirect';
import contactType from 'helpers/contact_type';
import { useAngularService } from 'helpers/get_angular_service';
import usePermissions from 'helpers/use_permissions';
import { useResponsive } from 'helpers/use_responsive';

const GlobalSearch = () => {
  const ReactModal = useAngularService('ReactModal');
  const TasksService = useAngularService('TasksService');
  const DrawerService = useAngularService('DrawerService');
  const FeatureAvailabilityService = useAngularService('FeatureAvailabilityService');
  const $rootScope = useAngularService('$rootScope');
  const $state = useAngularService('$state');

  const gym = useGym();
  const permissions = usePermissions();
  const [query, setQuery] = useState('');
  const [debouncedQuery] = useDebounce(query, 400);
  const [contacts, setContacts] = useState([]);
  const [focused, setFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const { isMobile } = useResponsive();

  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdownEl = document.querySelectorAll('.dropdown-menu--in-portal')[0];
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target) &&
        !dropdownEl?.contains(event.target)
      ) {
        setTimeout(() => {
          setQuery('');
          setContacts([]);
          setFocused(false);
        }, 200);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const isFeatureAvailable = (feature) => FeatureAvailabilityService.isAvailable(feature);

  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < 2) {
      setContacts([]);
      return;
    }

    setLoading(true);

    const url = sources.contacts(gym.id);

    api.get(url, { all_contacts_search: debouncedQuery }).then(
      (response) => {
        setContacts(response);
        setLoading(false);
      },
      () => {
        setLoading(false);
      },
    );
  }, [debouncedQuery, gym?.id]);

  const getBadge = (contact) => {
    return (
      <span
        className={classNames(
          'worx-badge',
          contactBadgeClass({
            type: contact.type,
            status: contact.status,
          }),
        )}
      />
    );
  };

  const clearInput = () => {
    setTimeout(() => {
      setQuery('');
      setContacts([]);
      setFocused(false);
    }, 100);
  };

  const onSelect = (contact) => {
    redirect('gym.contacts.edit', {
      contactType: contactType(contact.type),
      contactId: contact.id,
    });
  };

  const sendMessage = (messageType, contact) => {
    DrawerService.open({
      template: require('templates/gyms/contacts/send_message_modal.html.slim'),
      resolve: {
        messageParams: () => 'contact',
        contactId: () => contact.id,
        messageType: () => messageType,
      },
      controller: 'SendMessageModalCtrl',
    });
  };

  const onActionSelect = (action, contact) => {
    switch (action) {
      case 'view_profile': {
        onSelect(contact);
        break;
      }
      case 'add_note':
        ReactModal.open({
          component: 'AddContactNoteModal',
          drawer: true,
          props: {
            contact,
          },
        });

        break;
      case 'add_flag':
        ReactModal.open({
          component: 'AddContactFlagModal',
          drawer: true,
          props: {
            contact,
          },
        });
        break;
      case 'add_task':
        TasksService.create({ contactId: contact.id });
        break;
      case 'send_email':
        sendMessage('email', contact);
        break;
      case 'send_sms':
        sendMessage('sms', contact);
        break;
      case 'send_push_notification':
        sendMessage('push_notification', contact);
        break;
      case 'view_payments':
        redirect('gym.contacts.edit', {
          contactType: contactType(contact.type),
          contactId: contact.id,
          tab: 'payments',
        });
        break;
      case 'add_membership':
        redirect('gym.contacts.membership', {
          contactType: contactType(contact.type),
          contactId: contact.id,
        });
        break;
      case 'add_suspension':
        redirect('gym.contacts.suspension', {
          contactType: contactType(contact.type),
          contactId: contact.id,
          suspensionId: 'new',
        });
        break;
      case 'add_booking':
        redirect('gym.contacts.edit', {
          contactType: contactType(contact.type),
          contactId: contact.id,
          tab: 'attendance',
        }).then(() => {
          $rootScope.$emit('contact:addBooking');
        });
        break;
      case 'new_sale':
        redirect('gym.pos.sales.new', { contactId: contact.id });
        break;
      case 'quick_promote': {
        if ($state.is('gym.grading.gradings.new')) {
          $rootScope.$emit('gradings:quickPromote', contact);
        } else {
          redirect('gym.grading.gradings.new', {
            quickPromoteContactId: contact.id,
            quickPromoteContactName: contact.name,
          });
        }

        break;
      }
      case 'sign_new_waiver':
        redirect('gym.contacts.edit', {
          contactType: contactType(contact.type),
          contactId: contact.id,
          tab: 'waivers',
          action: 'sign_new_waiver',
        });
        break;
      default:
        break;
    }

    clearInput();
  };

  const isMember = (contact) => {
    return contact.type === 'member';
  };

  const isProspect = (contact) => {
    return contact.type === 'prospect';
  };

  const isNac = (contact) => {
    return contact.type === 'non_attending_contact';
  };

  const isPosAvailable = (contact) => {
    return isFeatureAvailable('pos') && permissions.create_pos_sales && !isProspect(contact);
  };

  const isGradingsAvailable = (contact) => {
    return permissions.perform_grading && gym.is_martial_arts && isMember(contact);
  };

  const isSignNewWaiverAvailable = () => {
    return (
      isFeatureAvailable('waivers') &&
      gym.gym_waiver_detail.enabled &&
      permissions.view_contact_profile_page &&
      (permissions.manage_signed_waivers || permissions.create_prefilled_waivers)
    );
  };

  const isViewPaymentsAvailable = (contact) => {
    return (
      permissions.view_member_payments &&
      permissions.view_contact_profile_page &&
      !isProspect(contact)
    );
  };

  const isAddBookingAvailable = (contact) => {
    return (
      !isNac(contact) && isFeatureAvailable('attendance') && permissions.view_contact_profile_page
    );
  };

  return (
    <div
      className={classNames('search-area-layout navbar-search-form globalsearch', {
        'full-size': focused || query,
      })}
      ref={wrapperRef}
    >
      <i className="search-icon" />
      <Input
        data-test-id="contact-search-input"
        value={query}
        onChange={(val) => setQuery(val as string)}
        onFocus={() => setFocused(true)}
        placeholder="Search..."
        className="search-area-input"
      />
      {query.length > 0 && (
        <div className="worx-search-results-dropdown is-visible">
          <table className="global-search__table">
            <tbody>
              {contacts.map((contact) => (
                <tr
                  className="global-search__item"
                  key={contact.id}
                  onClick={() => {
                    onSelect(contact);
                    clearInput();
                  }}
                >
                  <td>
                    <div className="flex align-center" style={{ padding: '4px 8px' }}>
                      <div className="global-search__item-cell">{getBadge(contact)}</div>
                      <div className="global-search__name">
                        <div className="global-search__item-cell bold">{contact.name}</div>
                        <div className="global-search__item-cell">{contact.email}</div>
                      </div>
                    </div>
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="text-right margin-right">
                      <Dropdown
                        buttonVariant={isMobile ? undefined : 'outline-primary'}
                        buttonType={isMobile ? 'icon-more' : undefined}
                        size="sm"
                        mobileFullWidth
                        options={_.compact([
                          permissions.view_contact_profile_page && {
                            value: 'view_profile',
                            label: 'View Profile',
                          },

                          isSignNewWaiverAvailable() && {
                            value: 'sign_new_waiver',
                            label: 'Sign new Waiver',
                          },

                          isAddBookingAvailable(contact) && {
                            value: 'add_booking',
                            label: 'Attendance: Add Booking',
                          },

                          isPosAvailable(contact) && {
                            value: 'new_sale',
                            label: 'POS: New Sale',
                          },

                          isGradingsAvailable(contact) && {
                            value: 'quick_promote',
                            label: 'Grading: Quick Promote',
                          },

                          permissions.send_comms && {
                            label: 'Comms',
                            sectionHeader: true,
                            options: _.compact([
                              permissions.send_comms &&
                                !contact.unsubscribed_from_email && {
                                  value: 'send_email',
                                  label: 'Send Email',
                                },
                              permissions.send_comms &&
                                !contact.unsubscribed_from_sms && {
                                  value: 'send_sms',
                                  label: 'Send SMS',
                                },
                              permissions.send_comms &&
                                gym.mobile_app_enabled && {
                                  value: 'send_push_notification',
                                  label: 'Send Push Notification',
                                },
                            ]),
                          },

                          !isProspect(contact) && {
                            label: 'Memberships',
                            sectionHeader: true,
                            options: _.compact([
                              isViewPaymentsAvailable(contact) && {
                                value: 'view_payments',
                                label: 'View Payments',
                              },
                              permissions.manage_memberships &&
                                !isProspect(contact) && {
                                  value: 'add_membership',
                                  label: 'Add Membership',
                                },
                              !isProspect(contact) && {
                                value: 'add_suspension',
                                label: 'Add Suspension',
                              },
                            ]),
                          },

                          {
                            label: 'General',
                            sectionHeader: true,
                            options: _.compact([
                              permissions.view_contact_profile_page && {
                                value: 'add_note',
                                label: 'Add Note',
                              },
                              permissions.manage_contact_flags && {
                                value: 'add_flag',
                                label: 'Add Flag',
                              },
                              permissions.manage_tasks && {
                                value: 'add_task',
                                label: 'Add Task',
                              },
                            ]),
                          },
                        ])}
                        onSelect={(action) => onActionSelect(action, contact)}
                      >
                        Actions
                      </Dropdown>
                    </div>
                  </td>
                </tr>
              ))}
              {!contacts.length && (
                <tr className="global-search__item">
                  <td>
                    <div className="flex align-center" style={{ padding: '4px 8px' }}>
                      <div className="global-search__item-cell">
                        {loading ? 'Loading...' : 'No results'}
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

angular.module('gym').value('GlobalSearch', GlobalSearch);
